import { MAP_OBJECTS, MAP_SHAPES, SKETCH_STYLE, TERRAIN, WALL_KINDS, cellKey, objectGroups, objectSketchColor } from '../../../shared/mapLayout.mjs';

/**
 * Draws a layout as the image generator's sketch on a 2D canvas context: flat terrain colors (dark grey where nothing
 * is painted), wall lines along square edges (doors as thick lime bars), stamped objects as outlined colored patches
 * (touching same-kind stamps merged) with one letter each, and each noted pin as a numbered circle. No grid lines; the
 * colors match describeLayoutForPrompt.
 */
export const drawLayoutSketch = (ctx, layout, width, height) => {
  const cw = width / layout.columns;
  const ch = height / layout.rows;
  const unit = Math.min(cw, ch);
  ctx.fillStyle = SKETCH_STYLE.unpainted.color;
  ctx.fillRect(0, 0, width, height);
  for (let y = 0; y < layout.rows; y += 1) {
    for (let x = 0; x < layout.columns; x += 1) {
      const terrain = TERRAIN[layout.terrain[cellKey(x, y)]];
      if (!terrain) continue;
      ctx.fillStyle = terrain.color;
      ctx.fillRect(x * cw, y * ch, Math.ceil(cw), Math.ceil(ch));
    }
  }
  // Each patch of touching same-kind stamps is one solid block with one outline and one letter, so the generator
  // paints one feature (a rubble field) rather than a pile per square.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const group of objectGroups(layout)) {
    const inGroup = new Set(group.cells.map((cell) => cellKey(cell.x, cell.y)));
    ctx.fillStyle = objectSketchColor(group.id);
    for (const cell of group.cells) ctx.fillRect(cell.x * cw, cell.y * ch, Math.ceil(cw), Math.ceil(ch));
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = unit * 0.06;
    ctx.setLineDash([]);
    ctx.beginPath();
    for (const { x, y } of group.cells) {
      if (!inGroup.has(cellKey(x, y - 1))) { ctx.moveTo(x * cw, y * ch); ctx.lineTo((x + 1) * cw, y * ch); }
      if (!inGroup.has(cellKey(x, y + 1))) { ctx.moveTo(x * cw, (y + 1) * ch); ctx.lineTo((x + 1) * cw, (y + 1) * ch); }
      if (!inGroup.has(cellKey(x - 1, y))) { ctx.moveTo(x * cw, y * ch); ctx.lineTo(x * cw, (y + 1) * ch); }
      if (!inGroup.has(cellKey(x + 1, y))) { ctx.moveTo((x + 1) * cw, y * ch); ctx.lineTo((x + 1) * cw, (y + 1) * ch); }
    }
    ctx.stroke();
    // The letter goes on the patch square nearest its middle.
    const midX = group.cells.reduce((sum, cell) => sum + cell.x + 0.5, 0) / group.cells.length;
    const midY = group.cells.reduce((sum, cell) => sum + cell.y + 0.5, 0) / group.cells.length;
    const anchor = group.cells.reduce((best, cell) => (Math.hypot(cell.x + 0.5 - midX, cell.y + 0.5 - midY)
      < Math.hypot(best.x + 0.5 - midX, best.y + 0.5 - midY) ? cell : best));
    const inside = inGroup.size === (group.right - group.left + 1) * (group.bottom - group.top + 1);
    ctx.fillStyle = '#111111';
    ctx.font = `bold ${Math.round(unit * 0.5)}px sans-serif`;
    ctx.fillText(MAP_OBJECTS[group.id].symbol, (inside ? midX : anchor.x + 0.5) * cw, (inside ? midY : anchor.y + 0.5) * ch);
  }
  // Walls first, then openings, so a door's bar sits over the rounded ends of the walls beside it.
  const order = { wall: 0, 'low-wall': 1, window: 2, door: 3 };
  const edges = Object.entries(layout.walls).filter(([, kind]) => WALL_KINDS[kind]).sort(([, a], [, b]) => order[a] - order[b]);
  for (const [key, kind] of edges) {
    const [xs, ys, dir] = key.split(',');
    const x = Number(xs) * cw;
    const y = Number(ys) * ch;
    ctx.strokeStyle = kind === 'wall' ? '#000000' : kind === 'door' ? SKETCH_STYLE.door : WALL_KINDS[kind].color;
    ctx.lineWidth = unit * (kind === 'wall' ? 0.28 : kind === 'low-wall' ? 0.14 : kind === 'door' ? 0.36 : 0.2);
    ctx.lineCap = kind === 'door' ? 'butt' : 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(dir === 'h' ? x + cw : x, dir === 'v' ? y + ch : y);
    ctx.stroke();
  }
  // Pins with a note: a numbered white circle centered on the pin's square (numbered as in the editor), so the
  // generator sees where each note belongs. The prompt says to paint the note, never the circle.
  layout.pins.forEach((pin, index) => {
    if (!pin.note) return;
    const cx = (Math.min(layout.columns - 1, Math.floor(pin.x)) + 0.5) * cw;
    const cy = (Math.min(layout.rows - 1, Math.floor(pin.y)) + 0.5) * ch;
    ctx.fillStyle = SKETCH_STYLE.pin.fill;
    ctx.strokeStyle = SKETCH_STYLE.pin.ring;
    ctx.lineWidth = unit * 0.06;
    ctx.beginPath();
    ctx.arc(cx, cy, unit * 0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = SKETCH_STYLE.pin.ring;
    ctx.font = `bold ${Math.round(unit * (index + 1 > 9 ? 0.36 : 0.48))}px sans-serif`;
    ctx.fillText(String(index + 1), cx, cy);
  });
};

/** The layout as a PNG data URL at the generator's image size for its grid shape. */
export const layoutSketchDataUrl = (layout) => {
  const [width, height] = (MAP_SHAPES[layout.shape]?.imageSize || '1024x1024').split('x').map(Number);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot draw the layout sketch.');
  drawLayoutSketch(ctx, layout, width, height);
  return canvas.toDataURL('image/png');
};
